import { useState, useEffect } from 'react';
import { BellRing } from 'lucide-react';
import { subscriptionService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const SubscribeButton = ({ vehicleId, vehicleType = 'car' }) => {
  const { userProfile } = useAuth();
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [celebrating, setCelebrating] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (!userProfile || !vehicleId) return;
    subscriptionService.check(vehicleId)
      .then(data => setSubscribed(data.is_subscribed))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userProfile, vehicleId]);

  if (!userProfile || loading) return null;

  const toggle = async (e) => {
    e.stopPropagation();
    const prev = subscribed;
    setSubscribed(!prev);
    if (!prev) {
      setCelebrating(true);
      setFeedback('¡Siguiendo!');
      setTimeout(() => setCelebrating(false), 700);
    } else {
      setFeedback('Ya no seguís');
    }
    setTimeout(() => setFeedback(null), 2000);
    try {
      if (prev) await subscriptionService.unsubscribe(vehicleId);
      else await subscriptionService.subscribe(vehicleId, vehicleType);
    } catch {
      setSubscribed(prev);
      setFeedback(null);
      setCelebrating(false);
    }
  };

  return (
    <div className="relative inline-flex items-center">
      <button onClick={toggle} className="relative flex items-center gap-1 p-1.5" aria-label={subscribed ? 'Dejar de seguir' : 'Seguir vehículo'}>
        {/* Burst particles on subscribe */}
        {celebrating && (
          <>
            {[...Array(6)].map((_, i) => (
              <span
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full bg-[#48a877]"
                style={{
                  top: '50%', left: '12px',
                  animation: `burst 600ms ease-out forwards`,
                  animationDelay: `${i * 40}ms`,
                  transform: `rotate(${i * 60}deg) translateY(0)`,
                }}
              />
            ))}
            <span className="absolute inset-0 rounded-full border-2 border-[#48a877] animate-ping opacity-50" />
          </>
        )}
        <BellRing
          className={`w-4 h-4 transition-all duration-300 ${celebrating ? 'scale-130 rotate-12' : 'scale-100 rotate-0'}`}
          fill={subscribed ? '#48a877' : 'none'}
          stroke={subscribed ? '#48a877' : '#8a8577'}
          style={{ transform: celebrating ? 'scale(1.3) rotate(12deg)' : undefined }}
        />
        {subscribed && !feedback && (
          <span className="text-[10px] text-[#48a877] font-medium">Siguiendo</span>
        )}
      </button>
      {feedback && (
        <span className="absolute left-full ml-1 whitespace-nowrap text-[10px] text-[#48a877] font-medium animate-fade-in">
          {feedback}
        </span>
      )}

      <style>{`
        @keyframes burst {
          0% { opacity: 1; transform: rotate(var(--r, 0deg)) translateY(0); }
          100% { opacity: 0; transform: rotate(var(--r, 0deg)) translateY(-14px); }
        }
        button .w-1\\.5:nth-child(1) { --r: 0deg; }
        button .w-1\\.5:nth-child(2) { --r: 60deg; }
        button .w-1\\.5:nth-child(3) { --r: 120deg; }
        button .w-1\\.5:nth-child(4) { --r: 180deg; }
        button .w-1\\.5:nth-child(5) { --r: 240deg; }
        button .w-1\\.5:nth-child(6) { --r: 300deg; }
      `}</style>
    </div>
  );
};

export default SubscribeButton;
